import React from 'react';
import { Modal } from '../ui/Modal';
import Badge from '../ui/Badge';
import { actionTone } from './gpsCalendarTones.js';
import { issueHref } from '../../utils/url.js';
import { safeFormatDate } from '../../utils/dateUtils.js';
import { interventionTime, UNKNOWN_ACTION } from '../../utils/gpsCalendarEvents.js';

// Europe/Paris, not UTC: the calendar buckets days on Paris time, the modal must agree.
const DAY_FORMAT = { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[9rem_1fr] gap-4 py-2.5">
    <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 pt-0.5">{label}</dt>
    <dd className="text-sm text-gray-900 dark:text-gray-100">{children}</dd>
  </div>
);

// Mounted by the calendar only while an intervention is selected, so `issue` is always set.
const GPSCalendarEventModal = ({ issue, onClose }) => {
  const href = issueHref(issue);
  const time = interventionTime(issue);

  return (
    <Modal isOpen onClose={onClose} title="Détail de l'intervention">
      <div className="space-y-5">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Date d'intervention
          </p>
          {/* Free-text Jira time appended verbatim — absent more often than not. */}
          <p className="mt-0.5 text-lg font-semibold first-letter:uppercase text-gray-900 dark:text-white">
            {safeFormatDate(issue.duedate, 'fr-FR', DAY_FORMAT)}
            {time && `, ${time}`}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge tone={actionTone(issue.request_type)} dot>
              {issue.request_type || UNKNOWN_ACTION}
            </Badge>
            {/* Raw Jira status, never a derived "technician assigned" claim. */}
            <Badge tone="gray">{issue.status}</Badge>
          </div>
        </div>

        <dl className="divide-y divide-gray-100 dark:divide-gray-700">
          <Row label="Magasin">{issue.intervention_point || '—'}</Row>
          {/* Some tickets carry no SAP code: say so rather than show a blank. */}
          <Row label="Code SAP">
            {issue.client_shop_id || (
              <span className="text-gray-500 dark:text-gray-400">Non renseigné</span>
            )}
          </Row>
          <Row label="Demandeur">{issue.reporter || '—'}</Row>
          {/* DWH tri-state BOOLEAN: NULL means the flag is not fed, never "no technician". */}
          <Row label="Technicien">
            {typeof issue.has_technician === 'boolean' ? (
              issue.has_technician ? 'Affecté' : 'Non affecté'
            ) : (
              <span className="text-gray-500 dark:text-gray-400">Non renseigné</span>
            )}
          </Row>
        </dl>

        <div className="flex justify-end border-t border-gray-100 dark:border-gray-700 pt-4">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-[#6e3ff3] px-3.5 py-2 text-sm font-medium
                         text-white transition-colors hover:bg-[#5b2fd9] focus:outline-none
                         focus-visible:ring-2 focus-visible:ring-[#6e3ff3] dark:focus-visible:ring-[#a78bfa]
                         focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
            >
              {issue.issue_key} — ouvrir dans Jira
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5h5v5M19 5l-8 8M10 5H6a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1v-4" />
              </svg>
            </a>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">Pas de ticket Jira</span>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default GPSCalendarEventModal;
